import { useState } from "react";
import { useLocale } from "../../../context/LanguageContext";
import { BROADCAST_SETTINGS } from "../constants";
import type { ChatIntegration, UpdateBroadcastSettingsInput } from "../types";

interface BroadcastSettingsProps {
  integration: ChatIntegration;
  onUpdate: (input: UpdateBroadcastSettingsInput) => Promise<void>;
  disabled?: boolean;
}

export function BroadcastSettings({
  integration,
  onUpdate,
  disabled = false,
}: BroadcastSettingsProps) {
  const [updating, setUpdating] = useState<string | null>(null);
  const { t } = useLocale();

  const handleToggle = async (
    key: (typeof BROADCAST_SETTINGS)[number]["key"],
    currentValue: boolean,
  ) => {
    setUpdating(key);
    try {
      await onUpdate({ [key]: !currentValue });
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-foreground">
        {t("integrations.broadcastSettings")}
      </h4>
      <div className="mt-2 space-y-2">
        {BROADCAST_SETTINGS.map((setting) => {
          const isEnabled = integration[setting.key];
          const isUpdating = updating === setting.key;

          return (
            <label
              key={setting.key}
              className={`flex items-start gap-3 rounded-md border border-border px-3 py-2.5 transition-colors ${
                disabled || isUpdating
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer hover:bg-muted/50"
              }`}
            >
              <input
                type="checkbox"
                checked={isEnabled}
                disabled={disabled || isUpdating}
                onChange={() => handleToggle(setting.key, isEnabled)}
                className="mt-0.5 h-4 w-4 rounded border-border text-primary accent-primary focus:ring-1 focus:ring-primary"
              />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground">
                  {t(setting.labelKey)}
                </span>
                <p className="text-xs text-muted-foreground">
                  {t(setting.descriptionKey)}
                </p>
              </div>
              {isUpdating && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}
